import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js';
import Color from 'color';
import '../../plugins/chartJsBarChartBackgroundPlugin';
import { format, getPropertyValue } from '../../../utils';
import { useGlobalValue } from '../../../state/GlobalProvider';
import styles from './ProfitAndLossChart.module.sass';
const TICKS_FONT_SIZE = 12;

const transform = (data, colors = [], i18n) => {
  const profit = [data.revenue.total];
  const cost = (data.cost.groups || []).map(({
    total
  }) => total);
  const secondaryColor = new Color(colors[1]);
  const primaryColor = new Color(colors[0]);
  const pFade = primaryColor.contrast(Color('black')) > 3 ? 'lighten' : 'darken';
  const sFade = secondaryColor.contrast(Color('white')) > 3 ? 'lighten' : 'darken';
  const revenueLabel = i18n`Revenue`;
  const revenueLegend = i18n`${revenueLabel.match(/\b(\w)/g).join('')}`;
  const revenueCodes = data.revenue.entries.map(({
    code
  }) => code);
  const firstCostLabel = i18n`${((data.cost.groups || [])[0] || {}).name || ''}`;
  const firstCostLegend = i18n`${firstCostLabel.replace(/[^0-9a-zA-Z\s]+/g, 'a').match(/\b(\w)/g).join('')}`;
  const firstCostCodes = (((data.cost.groups || [])[0] || {}).entries || []).map(({
    code
  }) => code);
  const secondCostLabel = i18n`${((data.cost.groups || [])[1] || {}).name || ''}`;
  const secondCostLegend = i18n`${secondCostLabel.match(/\b(\w)/g).join('')}`;
  const secondCostCodes = (((data.cost.groups || [])[1] || {}).entries || []).map(({
    code
  }) => code);
  const thirdCostLabel = i18n`${((data.cost.groups || [])[2] || {}).name || ''}`;
  const thirdCostLegend = i18n`${thirdCostLabel.match(/\b(\w)/g).join('')}`;
  const thirdCostCodes = (((data.cost.groups || [])[2] || {}).entries || []).map(({
    code
  }) => code);
  const fourthCostLabel = i18n`${((data.cost.groups || [])[3] || {}).name || ''}`;
  const fourthCostLegend = i18n`${fourthCostLabel.match(/\b(\w)/g).join('')}`;
  const fourthCostCodes = (((data.cost.groups || [])[3] || {}).entries || []).map(({
    code
  }) => code);
  const fivethCostLabel = i18n`${((data.cost.groups || [])[4] || {}).name || ''}`;
  const fivethCostLegend = i18n`${fivethCostLabel.match(/\b(\w)/g).join('')}`;
  const fivethCostCodes = (((data.cost.groups || [])[4] || {}).entries || []).map(({
    code
  }) => code);
  return {
    labels: [i18n`Revenue`, i18n`Cost`],
    max: [data.revenue.total, data.cost.total],
    datasets: [{
      label: 'first',
      data: [profit[0], cost[0]],
      labels: [revenueLabel, firstCostLabel],
      legend: [revenueLegend, firstCostLegend],
      links: [revenueCodes, firstCostCodes],
      backgroundColor: [primaryColor.hex(), secondaryColor.hex()],
      borderWidth: {
        bottom: 1
      },
      borderColor: colors[2],
      borderCapStyle: 'round'
    }, {
      label: 'second',
      data: [profit[1], cost[1]],
      labels: [revenueLabel, secondCostLabel],
      legend: [revenueLegend, secondCostLegend],
      links: [revenueCodes, secondCostCodes],
      backgroundColor: [primaryColor[pFade](0.1).hex(), secondaryColor[sFade](0.1).hex()],
      borderWidth: {
        bottom: 1
      },
      borderColor: colors[2],
      borderCapStyle: 'round'
    }, {
      label: 'third',
      data: [profit[2], cost[2]],
      labels: [revenueLabel, thirdCostLabel],
      legend: [revenueLegend, thirdCostLegend],
      links: [revenueCodes, thirdCostCodes],
      backgroundColor: [primaryColor[pFade](0.2).hex(), secondaryColor[sFade](0.2).hex()],
      borderWidth: {
        bottom: 1
      },
      borderColor: colors[2],
      borderCapStyle: 'round'
    }, {
      label: 'fourth',
      data: [profit[3], cost[3]],
      labels: [revenueLabel, fourthCostLabel],
      legend: [revenueLegend, fourthCostLegend],
      links: [revenueCodes, fourthCostCodes],
      backgroundColor: [primaryColor[pFade](0.3).hex(), secondaryColor[sFade](0.3).hex()],
      borderColor: colors[2],
      borderCapStyle: 'round'
    }, {
      label: 'fiveth',
      data: [profit[4], cost[4]],
      labels: [revenueLabel, fivethCostLabel],
      legend: [revenueLegend, fivethCostLegend],
      links: [revenueCodes, fivethCostCodes],
      backgroundColor: [primaryColor[pFade](0.3).hex(), secondaryColor[sFade](0.4).hex()],
      borderColor: colors[2],
      borderCapStyle: 'round'
    }]
  };
};

const getLineValue = (data, colors) => {
  const profit = [data.revenue.total];
  const cost = data.cost.total;
  const lineValue = profit > cost ? cost : profit;
  return {
    datasets: [{
      label: 'line',
      data: [lineValue, lineValue],
      borderColor: colors[0],
      backgroundColor: colors[0],
      borderWidth: 2,
      // borderDash: [10, 8],
      fill: false,
      spanGaps: false,
      backgroundColor: 'transparent',
      borderCapStyle: 'round',
      type: 'line',
      radius: 0,
      pointRadius: 0
    }]
  };
};

const renderChart = (canvas, cData, colors, i18n, goto, link) => {
  let chartData = null;
  const style = getComputedStyle(canvas);
  const ctx = canvas.getContext('2d');
  const chartColors = [getPropertyValue("--chart-primary-color").trim(), getPropertyValue("--chart-secondary-color").trim(), getPropertyValue("--chart-background-color").trim()];
  chartData = cData ? transform(cData, [chartColors[0], chartColors[1], chartColors[2]], i18n) : null;
  const max = cData.cost.total < cData.revenue.total ? cData.revenue.total : cData.cost.total;
  const minTotal = (cData.cost.groups || []).map(elem => {
    return elem.total;
  });
  const min = Math.min(...minTotal); // const absMax = chartData.max[0] > chartData.max[1] ? chartData.max[0] : chartData.max[1]

  const chart = new Chart(ctx, {
    type: 'bar',
    data: chartData,
    options: {
      layout: {
        padding: {
          top: 22,
          left: 10,
          right: 10
        }
      },
      hover: {
        mode: 'nearest',
        animationDuration: 0,
        onHover: (e, chartElements) => {
          e.target.style.cursor = chartElements[0] ? 'pointer' : 'default';
          e.target.nextSibling.style.opacity = chartElements[0] ? '0' : '1';
          e.target.nextSibling.style.transition = chartElements[0] ? 'opacity 200ms linear' : 'opacity 200ms 200ms';
        }
      },
      onClick: evt => {
        const activePoints = chart.getElementAtEvent(evt);
        const tooltipItem = activePoints[0] || {};
        const dataset = chartData.datasets[tooltipItem._datasetIndex] || {
          labels: []
        };
        const filterValues = (dataset.links || [])[tooltipItem._index];

        if (link && goto && filterValues) {
          let filter = [{
            column: link.filterColumn,
            filters: filterValues.map(value => ({
              value
            }))
          }];

          if (link.filters) {
            const othersFilters = link.filters.map(item => ({
              column: item.column,
              filters: [{
                value: item.value
              }]
            }));
            filter = [...filter, ...othersFilters];
          }

          setTimeout(() => {
            goto(link.to, filter);
          });
        }
      },
      tooltips: {
        enabled: true,
        callbacks: {
          title: function (tooltipItem, data) {
            const dataset = data.datasets[tooltipItem[0].datasetIndex] || {
              labels: []
            };
            return (dataset.labels || [])[tooltipItem[0].index];
          },
          label: function (tooltipItem) {
            return format(tooltipItem.yLabel, 'short');
          }
        },
        displayColors: false,
        backgroundColor: colors.$chartTooltipBackgroundColor,
        borderColor: colors.$chartTooltipBorderColor,
        borderWidth: 1,
        cornerRadius: 0
      },
      animation: {
        duration: 0
      },
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false
      },
      scales: {
        xAxes: [{
          stacked: true,
          gridLines: {
            display: false,
            drawBorder: false
          },
          maxBarThickness: 100,
          categoryPercentage: 0.8,
          barPercentage: 0.8,
          ticks: {
            beginAtZero: true,
            fontStyle: 'bold',
            fontColor: style.color
          }
        }],
        yAxes: [{
          stacked: true,
          gridLines: {
            display: true,
            drawBorder: false,
            color: style.borderColor,
            zeroLineColor: style.borderColor
          },
          ticks: {
            padding: 10,
            fontStyle: 'normal',
            max: max,
            min: min,
            fontColor: style.color,
            fontSize: TICKS_FONT_SIZE,
            callback: function (label, index, labels) {
              return index === 0 || label === 0 || index === labels.length - 1 ? '' : null;
            }
          }
        }, {
          stacked: true,
          position: 'right',
          gridLines: {
            display: false,
            drawBorder: false
          },
          ticks: {
            padding: 10,
            fontStyle: 'bold',
            fontColor: 'transparent',
            fontSize: TICKS_FONT_SIZE,
            callback: function (_, index) {
              return index === 0 ? '' : null;
            }
          }
        }]
      },
      plugins: {
        chartJsPluginBarChartBackground: {
          color: colors.$chartBackgroundColor
        }
      }
    },
    plugins: [{
      afterDatasetsDraw: function (chartInstance) {
        const innerCtx = chartInstance.ctx;
        innerCtx.font = Chart.helpers.fontString(parseFloat(style.fontSize), style.fontWeight, style.fontFamily);
        innerCtx.textAlign = 'center';
        innerCtx.textBaseline = 'bottom';
        const bars = chartInstance.controller.getDatasetMeta(1).data;
        innerCtx.fillStyle = colors.$chartTextColor;
        bars.forEach((bar, i) => {
          const x = bar._model.x;
          const value = chartData.max[i] || 0;
          value && innerCtx.fillText(format(value, 'short'), x, 14);
        });
        innerCtx.fillStyle = colors.$chartTextColor;
        chartInstance.config.data.datasets.forEach((dataset, i) => {
          const meta = chartInstance.controller.getDatasetMeta(i);
          meta.data.forEach(function (bar, index) {
            const data = dataset.data[index];
            const label = (dataset.legend || [])[index];

            if (data) {
              innerCtx.fillText(label, bar._model.x, bar._model.y + (bar._model.base - bar._model.y) / 2 + 5);
            }
          });
        });
      }
    }]
  });
  return chart;
};

const renderLine = (canvas, cData, chart, colors) => {
  const style = getComputedStyle(canvas);
  const chartData = cData ? getLineValue(cData, [colors.$legendChartColor]) : null;
  const ctx = canvas.getContext('2d');
  const max = chart.scales['y-axis-0'].max;
  const min = chart.scales['y-axis-0'].min;
  const lineToChart = cData.net;
  return new Chart(ctx, {
    type: 'line',
    data: chartData,
    options: {
      layout: {
        padding: {
          top: 20,
          left: 32,
          right: 32
        }
      },
      hover: {
        animationDuration: 0
      },
      tooltips: {
        enabled: false
      },
      animation: {
        duration: 0,
        onComplete: function () {
          const chartInstance = this.chart;
          const innerCtx = chartInstance.ctx;
          innerCtx.font = Chart.helpers.fontString(parseFloat(style.fontSize), style.fontWeight, style.fontFamily);
          innerCtx.textAlign = 'right';
          innerCtx.textBaseline = 'bottom';
          innerCtx.fillStyle = colors.$chartTextColor;
          const dataset = chartInstance.data.datasets[0] || {
            data: []
          };
          const points = chartInstance.controller.getDatasetMeta(0).data || [];
          const lastPoint = points[points.length - 1];
          const value = dataset.data[dataset.data.length - 1];

          if (lastPoint && value) {
            innerCtx.fillText(format(lineToChart, 'short'), lastPoint._model.x, lastPoint._model.y - 8);
          }
        }
      },
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false
      },
      scales: {
        xAxes: [{
          display: true,
          gridLines: {
            display: false,
            drawBorder: false
          }
        }],
        yAxes: [{
          display: false,
          gridLines: {
            display: false,
            drawBorder: false
          },
          ticks: {
            suggestedMax: max,
            suggestedMin: min,
            min: min,
            max: max
          }
        }]
      }
    }
  });
};

const ProfitAndLossChart = ({
  data,
  link
}) => {
  const {
    i18n,
    theme,
    goto
  } = useGlobalValue();
  const canvas = useRef(null);
  const lineCanvas = useRef(null);
  const {
    colors = {}
  } = theme || {};
  useEffect(() => {
    const chart = canvas.current && data && renderChart(canvas.current, data || {}, colors, i18n, goto, link);
    const lineChart = chart && lineCanvas.current && data && renderLine(lineCanvas.current, data, chart, colors);
    return () => {
      chart && chart.destroy();
      lineChart && lineChart.destroy();
    };
  }, [canvas, data, colors, i18n]);
  return /*#__PURE__*/React.createElement("div", {
    className: styles['root']
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("canvas", {
    ref: canvas
  }), /*#__PURE__*/React.createElement("canvas", {
    ref: lineCanvas,
    style: {
      pointerEvents: 'none'
    }
  })));
};

export default ProfitAndLossChart;